import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { ArrowRight, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

const FIN_LIT_BOOKS = [
  { title: "The 12 Tenets of the Richest Man in Babylon", desc: "Timeless principles of wealth-building distilled for modern application.", cover: "https://customer-assets.emergentagent.com/job_benmagee-preview/artifacts/pbhxubee_F759BD83-5A3B-4082-BC2A-C38791FE0000.PNG", link: "https://a.co/d/0a1Lsk7l" },
  { title: "In Wealth We Trust", desc: "A direct exploration of the mindsets and systems that create lasting wealth.", cover: "https://customer-assets.emergentagent.com/job_benmagee-preview/artifacts/x1zl8rf3_C3227A5B-F543-41E8-990C-6C15639A7E0A.PNG", link: "https://a.co/d/0bSoo8n8" },
  { title: "Understanding the Numbers\u2122 (2nd Ed.)", desc: "Financial literacy fundamentals for those ready to take control.", badge: "First Step Act Validated", cover: "https://customer-assets.emergentagent.com/job_063e6bfb-ec62-4703-a8ad-86932ecf54db/artifacts/uqei5u0n_IMG_5785.jpeg", link: "https://a.co/d/0fcgW9O6" },
  { title: "Poverty Is A Mindset, Wealth Is A System", desc: "Dismantling the myths and building the framework for wealth creation.", cover: "https://customer-assets.emergentagent.com/job_benmagee-preview/artifacts/z7vamvbz_Poverty_Is_A_Mindset_Wealth_Is_A_System_KDP_SAFE_6x9_300DPI.jpg", link: "https://a.co/d/07s8QBGV" },
  { title: "The Great Wealth Transfer\u2122", desc: "The comprehensive acquisition playbook for minority entrepreneurs.", badge: "Coming 2027", cover: "https://customer-assets.emergentagent.com/job_063e6bfb-ec62-4703-a8ad-86932ecf54db/artifacts/ulccn5tw_IMG_5784.jpeg" },
];

const COMM_BOOKS = [
  { num: "Book 1", title: "The Art of Small Talk", cover: "https://customer-assets.emergentagent.com/job_benmagee-preview/artifacts/dwf08g25_artofsmalltalk-cover.PNG", link: "https://a.co/d/0c3WE4mk" },
  { num: "Book 2", title: "The Art of Conversation", cover: "https://customer-assets.emergentagent.com/job_benmagee-preview/artifacts/f1puz3kc_8532A7AA-E55D-456B-9172-440F3DB28520.PNG", link: "https://a.co/d/00pXXThp" },
  { num: "Book 3", title: "The Art of Listening", cover: "https://customer-assets.emergentagent.com/job_benmagee-preview/artifacts/8i61vzoy_kdp-book-cover.PNG", link: "https://a.co/d/06UV0H07" },
  { num: "Book 4", title: "The Art of Asking Questions", cover: "https://customer-assets.emergentagent.com/job_benmagee-preview/artifacts/yf4v0vc9_book-cover.PNG", link: "https://a.co/d/0fwjo48A" },
  { num: "Book 5", title: "The Art of Persuasion", cover: "https://customer-assets.emergentagent.com/job_benmagee-preview/artifacts/ot23ed4a_95BD4385-8077-4EA0-9818-6F06077E626B.PNG", link: "https://a.co/d/01EEgXiu" },
  { num: "Book 6", title: "The Art of Negotiation", cover: "https://customer-assets.emergentagent.com/job_benmagee-preview/artifacts/aypixhgv_book-cover.PNG", link: "https://a.co/d/0fdCKp06" },
  { num: "Advanced", title: "The Art of Silence", subtitle: "How Strategic Silence Builds Authority and Influence", cover: "https://customer-assets.emergentagent.com/job_benmagee-preview/artifacts/km6b9v0n_Canva.jpeg", link: "https://a.co/d/04959npG" },
];

export default function Books() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState("");

  const handleEarlyReader = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/subscribers", { email, name, list_type: "early_reader" });
      setStatus("You're on the list!");
      setEmail("");
      setName("");
      setTimeout(() => setStatus(""), 4000);
    } catch { setStatus("Error. Try again."); }
  };

  return (
    <div>
      {/* HERO */}
      <SEO
        title="Books"
        description="Books by Dr. Ben Magee — Financial Literacy Series, Communication Mastery Series, and The Great Wealth Transfer. Plain language. Real mechanics. Written for people who are ready to act."
        keywords="Dr. Ben Magee books, Great Wealth Transfer, financial literacy, Communication Mastery Series, minority entrepreneurship, business acquisition"
        page="books"
        canonical="https://drbenmagee.com/books"
      />
      <section className="bg-[#1B2A4A] py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="w-1 h-12 bg-[#C9A84C] mb-8" />
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight" data-testid="books-hero-title">
            Books That Equip, Not Just Inspire
          </h1>
          <p className="mt-4 text-base md:text-lg text-white/60">
            Plain language. Real mechanics. Written for people who are ready to act.
          </p>
        </div>
      </section>

      {/* FEATURED BOOK */}
      <section className="py-20 md:py-32 bg-[#F5EDD6]" data-testid="books-featured">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-[#1B2A4A] text-white rounded-sm mb-4 hover:bg-[#1B2A4A]">Forthcoming &mdash; 2027</Badge>
              <h2 className="font-heading font-black text-2xl sm:text-3xl lg:text-4xl text-[#1B2A4A] tracking-tight mb-4">
                THE GREAT WEALTH TRANSFER&trade;
              </h2>
              <p className="text-base text-[#1B2A4A]/70 italic mb-4">
                How Minority Entrepreneurs Can Claim Their Share of the Largest Wealth Shift in American History
              </p>
              <p className="text-sm text-[#1B2A4A]/70 leading-relaxed mb-4">
                By Dr. Ben Magee | Reform Publishing LLC
              </p>
              <p className="text-sm text-[#1B2A4A]/70 leading-relaxed mb-8">
                The comprehensive, action-oriented playbook for minority and underserved entrepreneurs who want to participate in the largest wealth redistribution event in American history &mdash; not as observers, but as acquirers, owners, and legacy builders.
              </p>
              <form onSubmit={handleEarlyReader} className="space-y-3 max-w-md">
                <Input type="text" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} data-testid="books-early-name" className="rounded-sm border-[#1B2A4A]/20" />
                <Input type="email" placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="books-early-email" className="rounded-sm border-[#1B2A4A]/20" />
                <Button type="submit" data-testid="books-early-submit" className="bg-[#C9A84C] text-[#1B2A4A] hover:bg-[#b59744] hover:text-white font-semibold rounded-sm">
                  Join the Early Reader List
                </Button>
                {status && <p className="text-sm text-[#1B2A4A] font-medium">{status}</p>}
              </form>
            </div>
            <div className="flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_063e6bfb-ec62-4703-a8ad-86932ecf54db/artifacts/ulccn5tw_IMG_5784.jpeg" 
                alt="The Great Wealth Transfer by Dr. Ben Magee"
                className="h-[480px] object-contain rounded-sm shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FINANCIAL LITERACY SERIES */}
      <section className="py-20 md:py-32 bg-white" data-testid="books-financial-literacy">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-4">Financial Literacy Series</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#1B2A4A] tracking-tight mb-12">
            Building Wealth Through Knowledge
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {FIN_LIT_BOOKS.map((book, i) => (
              <motion.div
                key={book.title}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                className="bg-white border border-[#1B2A4A]/10 rounded-sm overflow-hidden flex flex-col group hover:-translate-y-1 transition-transform duration-300"
              >
                {book.cover ? (
                  <div className="relative bg-[#1B2A4A] flex items-center justify-center p-6">
                    <img src={book.cover} alt={book.title} className="h-56 object-contain rounded-sm shadow-lg group-hover:scale-105 transition-transform duration-300" />
                  </div>
                ) : (
                  <div className="bg-[#1B2A4A] h-56 flex items-center justify-center p-6">
                    <div className="text-center">
                      <div className="w-8 h-1 bg-[#C9A84C] mx-auto mb-3" />
                      <p className="font-heading font-bold text-white text-sm">{book.title}</p>
                      {book.badge && <p className="text-[#C9A84C] text-xs mt-2">{book.badge}</p>}
                    </div>
                  </div>
                )}
                <div className="p-6 flex flex-col flex-1">
                  {book.badge && (
                    <Badge className="self-start bg-[#C9A84C] text-[#1B2A4A] rounded-sm mb-3 text-xs hover:bg-[#C9A84C]">{book.badge}</Badge>
                  )}
                  <h3 className="font-heading font-semibold text-lg text-[#1B2A4A] mb-2">{book.title}</h3>
                  <p className="text-sm text-[#1B2A4A]/60 leading-relaxed flex-1">{book.desc}</p>
                  {book.link ? (
                    <a href={book.link} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm" className="mt-4 self-start rounded-sm border-[#1B2A4A]/20 text-[#1B2A4A] hover:bg-[#E8EDF5]" data-testid={`buy-book-${i}`}>
                        <ExternalLink className="h-4 w-4 mr-2" /> Buy on Amazon
                      </Button>
                    </a>
                  ) : (
                    <Button variant="outline" size="sm" disabled className="mt-4 self-start rounded-sm border-[#1B2A4A]/20 text-[#1B2A4A]/40" data-testid={`buy-book-${i}`}>
                      Coming Soon
                    </Button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMUNICATION MASTERY SERIES */}
      <section className="py-20 md:py-32 bg-[#E8EDF5]" data-testid="books-communication">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-4">Communication Mastery Series&trade;</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#1B2A4A] tracking-tight mb-12">
            The Art of Effective Communication
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
            {COMM_BOOKS.map((book, i) => (
              <motion.div
                key={book.title}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                className="bg-white border border-[#1B2A4A]/10 rounded-sm overflow-hidden group hover:-translate-y-1 transition-transform duration-300"
              >
                <div className="relative bg-[#F5EDD6] flex items-center justify-center p-4">
                  <img src={book.cover} alt={book.title} className="h-48 object-contain rounded-sm shadow-md group-hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="p-4">
                  <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#C9A84C] mb-1">{book.num}</p>
                  <h3 className="font-heading font-semibold text-sm text-[#1B2A4A]">{book.title}</h3>
                  {book.subtitle && <p className="text-xs text-[#1B2A4A]/50 mt-1">{book.subtitle}</p>}
                  {book.link && (
                    <a href={book.link} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm" className="mt-3 rounded-sm border-[#1B2A4A]/20 text-[#1B2A4A] hover:bg-[#E8EDF5] text-xs" data-testid={`buy-comm-${i}`}>
                        <ExternalLink className="h-3 w-3 mr-1.5" /> Buy on Amazon
                      </Button>
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MEMOIR */}
      <section className="py-20 md:py-32 bg-white" data-testid="books-memoir">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute -inset-2 bg-[#C9A84C]/15 rounded-sm blur-md" />
                <img
                  src="https://customer-assets.emergentagent.com/job_benmagee-preview/artifacts/8cr8n9ki_2BB0FAEB-47B2-44A2-B712-0973B3BBA8BF.PNG"
                  alt="Bent But Not Broken"
                  className="relative h-80 object-contain rounded-sm shadow-xl"
                />
              </div>
            </div>
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-4">Memoir & Personal Development</p>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#1B2A4A] tracking-tight mb-3">
                Bent But Not Broken
              </h2>
              <p className="text-base text-[#1B2A4A]/60 italic mb-4">A Memoir of Reformation and Atonement</p>
              <p className="text-sm text-[#1B2A4A]/70 leading-relaxed mb-6">On Resilience and Ruin. A deeply personal account of the forces that shape, break, and ultimately rebuild a life committed to service.</p>
              <Badge className="bg-[#1B2A4A] text-white rounded-sm hover:bg-[#1B2A4A]">Coming Soon</Badge>
            </div>
          </div>
        </div>
      </section>

      {/* BULK ORDERS */}
      <section className="py-16 md:py-24 bg-[#1B2A4A]" data-testid="books-bulk">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight mb-4">Bulk Orders</h2>
          <p className="text-sm text-white/60 mb-8 max-w-2xl mx-auto">
            Institutional pricing available for CDFIs, SBDCs, HBCUs, reentry programs, and corporate DEI initiatives. Contact Phoenix Marketing & Distribution LLC for bulk orders and curriculum licensing.
          </p>
          <a href="/contact">
            <Button data-testid="bulk-orders-btn" className="bg-[#C9A84C] text-[#1B2A4A] hover:bg-[#b59744] hover:text-white font-semibold rounded-sm px-8">
              Contact for Bulk Orders <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
